// Client-side structured logging to PostHog Logs.
//
// Sibling of `log-server.ts` with the same typed-event contract: feature code
// calls `logClient(level, event, attributes)` and nothing else touches the
// SDK's logs API. Where the server helper hand-builds an OTel record, the
// browser SDK already ships one (`posthog.captureLog` / `posthog.logger.*`),
// so this wrapper only adds the taxonomy, the scrubbing, and the no-op guards.
//
// WHAT TO USE THIS FOR
// Client-side operational detail with no other trace: a fallback rendered, a
// mutation failed and the UI recovered, a resume produced nothing. Not for
// product analytics (use `track()`), and not for exceptions — those already
// land in PostHog Errors via `capture_exceptions: true` with source maps.
//
// WHAT THE SDK STAMPS FOR FREE (why there is no `ids` option here, unlike
// `logServer`): `captureLog` auto-attaches `posthogDistinctId`, `sessionId`,
// `url.full`, `screen.name` and `feature_flags` — the same attribute keys the
// server helper stamps manually — so client records filter by person and jump
// to the session replay with no extra wiring.
//
// DELIVERY: records are buffered (flush every 3s, or at 100 records) and are
// NOT flushed synchronously on emit. For a log fired right before navigation
// the buffer may be lost; there is no pageleave hook wired yet. If that ever
// matters, call `posthog.flushLogs("sendBeacon")` at the call site.
//
// CONSENT: client logs ride the ungated cookieless tier, same posture as
// `capture_exceptions` (free-form text, nothing stored on device) — see the
// decision log in `.cursor/plans/posthog-otel-logs.md` and
// `docs/observability.md`.
//
// PII RULE — READ BEFORE ADDING A CALL SITE
// Identical to the server helper, and the stakes are higher: the client runs
// where customers type names. The `event` name is a static string from
// `MarkaLogEvent`; attributes are metadata only (IDs, enums, booleans,
// counts). `before_send` does NOT run for logs — `scrubPiiText` here is the
// ONLY guardrail, and it is a coarse regex. The rule is upstream: don't pass
// PII. Never log the configurator's text lines, names, or emails.

import posthog from "posthog-js";

import type { LogAttributes, LogAttributeValue, LogLevel, MarkaLogEvent } from "./log-events";
import { scrubPiiText } from "./scrub";

/** What actually goes to the SDK. `undefined` is dropped, not represented. */
type EmittedValue = string | number | boolean | null;

function scrubAttributeValue(value: LogAttributeValue): EmittedValue {
  return typeof value === "string" ? scrubPiiText(value) : (value ?? null);
}

/**
 * Emit one structured log record from the browser.
 *
 * No-ops during SSR and before `posthog.init()` has finished (token missing,
 * SSR pass) — same guard as `track()` in `events.ts` — so callers can wire it
 * unconditionally. Never throws: a logging failure must not take down the
 * interaction being logged.
 */
export function logClient(
  level: LogLevel,
  event: MarkaLogEvent,
  attributes: LogAttributes = {}
): void {
  const scrubbed: Record<string, EmittedValue> = { event };
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== undefined) {
      scrubbed[key] = scrubAttributeValue(value);
    }
  }

  // Local dev mirror. Unlike the server helper this is dev-only: in
  // production a console line is noise nobody collects (console capture is
  // deliberately off, see instrumentation-client.ts), while in dev there is
  // usually no PostHog project to read.
  if (process.env.NODE_ENV === "development") {
    console[level](JSON.stringify({ level, ...scrubbed, service: "marka-web" }));
  }

  if (typeof window === "undefined") {
    return;
  }
  // `__loaded` flips true after `posthog.init(...)` finishes — same check as
  // `track()`: skip rather than risk silent queue-and-drop semantics.
  if (!(posthog as unknown as { __loaded?: boolean }).__loaded) {
    return;
  }

  try {
    // Body = event name, mirroring `logServer`: `searchTerm` and the `event`
    // attribute agree, and there is no second place for text to leak in.
    posthog.logger[level](event, scrubbed);
  } catch {
    // Swallowed on purpose — see "Never throws" above.
  }
}
